import { useEffect, useState } from "react";
import { Box, Typography, Button, IconButton, Tooltip } from "@mui/material";
import { ChevronLeft, ChevronRight, Info } from "lucide-react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { fetchRoomsCalendar, updateRatePrice, createRatePrice, updateAvailability, updateStatus } from "./inventoryService";
import InventoryGrid from "./InventoryGrid";
import BulkEditDrawer from "./BulkEditDrawer";
import { eurToKmf, formatKmf } from "./utils/currency"; // 🆕 adapter le chemin si besoin

const API = "https://api.komorotel.com/api";

/* 🆕 PÉRIODES — unité de vente d'un tarif.
   La barre de saisie du bas est le dernier endroit où l'hébergeur voit son
   montant avant de l'enregistrer : elle doit dire ce qu'il est en train de
   fixer, le prix d'une nuit ou celui d'un mois entier. */
const UNIT = {
  short_stay: { label: "nuit",    nights: 1  },
  weekly:     { label: "semaine", nights: 7  },
  monthly:    { label: "mois",    nights: 30 },
};

const normalizeStayType = (t) =>
  t === "long_stay" ? "monthly" : (["short_stay", "weekly", "monthly"].includes(t) ? t : "short_stay");

const unitOf = (stayType) => UNIT[normalizeStayType(stayType)];
const isPeriodPlan = (stayType) => normalizeStayType(stayType) !== "short_stay";

// 🆕 DATES — formatage en heure LOCALE.
//
// toISOString() convertit d'abord en UTC : à Paris (UTC+2 l'été), le
// 1er septembre à minuit devient le 31 août à 22h, et toute la grille
// se décalait d'un jour. La conversion se fait donc à la main.
const pad2 = (n) => String(n).padStart(2, "0");
const localISO = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const Calendar = ({ setActivePage, setNeedRatePlanMsg }) => {
  const { hotelId } = useParams();

  const [rooms, setRooms] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [startIndex, setStartIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  const [selectedDays, setSelectedDays] = useState({ tarif: [], inventory: [], status: [] });
  const [editType, setEditType] = useState(null);
  const [editModeRoom, setEditModeRoom] = useState(null);
  const [editRatePlan, setEditRatePlan] = useState(null);
  const [newValue, setNewValue] = useState("");

  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);

  const [bulkDrawer, setBulkDrawer] = useState({ open: false, type: null, roomId: null, ratePlanId: null });
  const [bulkValue, setBulkValue] = useState("");
  const [bulkEditOpen, setBulkEditOpen] = useState(false);

  const daysPerPage = 14;

  // ─────────────────────────────────────────
  // FETCH
  // ─────────────────────────────────────────
  const loadCalendar = async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const startDate = localISO(new Date(year, month, 1));
      const endDate = localISO(new Date(year, month + 1, 0));

      const [calendarData, ratePlansRes] = await Promise.all([
        fetchRoomsCalendar(hotelId, startDate, endDate),
        axios.get(`${API}/rateplans/hotel/${hotelId}`, { withCredentials: true })
      ]);

      setRooms(calendarData);
      setRatePlans(ratePlansRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCalendar();
  }, [hotelId, currentDate]);

  // ─────────────────────────────────────────
  // NAVIGATION
  // ─────────────────────────────────────────
  const daysOfMonth = (() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysCount = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: daysCount }, (_, i) => localISO(new Date(year, month, i + 1)));
  })();

  const changeMonth = (delta) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + delta, 1));
    setStartIndex(0);
    resetSelection();
  };

  const resetSelection = () => {
    setSelectedDays({ tarif: [], inventory: [], status: [] });
    setEditType(null);
    setEditModeRoom(null);
    setEditRatePlan(null);
    setNewValue("");
  };

  // ─────────────────────────────────────────
  // SAVE (sélection manuelle)
  // ─────────────────────────────────────────
  const handleSave = async () => {
    if (!newValue || !editType || !editModeRoom) return;

    const indices = selectedDays[editType];
    if (!indices?.length) return;

    const startDate = daysOfMonth[startIndex + Math.min(...indices)];
    const endDateRaw = daysOfMonth[startIndex + Math.max(...indices)];
    const endDate = localISO(new Date(new Date(`${endDateRaw}T12:00:00`).getTime() + 86400000));

    try {
      if (editType === "tarif") {
        const room = rooms.find(r => r.roomId === editModeRoom);
        const firstDay = room?.days[startIndex + Math.min(...indices)];
        const existingRate = firstDay?.rates?.find(r =>
          (r.ratePlanId?._id || r.ratePlanId)?.toString() === editRatePlan
        );

        if (existingRate?.ratePriceId) {
          await updateRatePrice(existingRate.ratePriceId, Number(newValue));
        } else {
          await createRatePrice(editModeRoom, editRatePlan, Number(newValue), startDate, endDate);
        }
      }

      if (editType === "inventory") {
        await updateAvailability(editModeRoom, startDate, endDate, Number(newValue));
      }

      if (editType === "status") {
        await updateStatus(editModeRoom, startDate, endDate, newValue);
      }

      await loadCalendar();
      resetSelection();
    } catch (err) {
      console.error(err);
    }
  };

  // ─────────────────────────────────────────
  // BULK (drawer par room)
  // ─────────────────────────────────────────
  const handleApplyBulk = async () => {
    if (!bulkValue || !bulkDrawer.roomId) return;

    const startDate = daysOfMonth[0];
    const endDate = localISO(
      new Date(new Date(`${daysOfMonth[daysOfMonth.length - 1]}T12:00:00`).getTime() + 86400000)
    );

    try {
      if (bulkDrawer.type === "price") {
        if (!bulkDrawer.ratePlanId) return;
        await createRatePrice(bulkDrawer.roomId, bulkDrawer.ratePlanId, Number(bulkValue), startDate, endDate);
      }

      if (bulkDrawer.type === "inventory") {
        await updateAvailability(bulkDrawer.roomId, startDate, endDate, Number(bulkValue));
      }

      await loadCalendar();
      setBulkDrawer({ open: false });
      setBulkValue("");
    } catch (err) {
      console.error(err);
    }
  };

  // ─────────────────────────────────────────
  // BULK EDIT (multi-rooms)
  // ─────────────────────────────────────────
  const handleBulkApply = async ({ roomIds, startDate, endDate, type, value, ratePlanId }) => {
    const start = localISO(startDate);
    const end = localISO(new Date(endDate.getTime() + 86400000));

    for (const roomId of roomIds) {
      if (type === "price" && ratePlanId) {
        await createRatePrice(roomId, ratePlanId, value, start, end);
      }
      if (type === "inventory") {
        await updateAvailability(roomId, start, end, value);
      }
      if (type === "status") {
        await updateStatus(roomId, start, end, value);
      }
    }

    await loadCalendar();
  };

  const monthLabel = currentDate.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

  // 🆕 MISE EN PAGE — la dernière page d'un mois contient souvent moins de
  // 14 jours. En laissant repeat(14), l'en-tête et les grilles gardaient
  // 14 colonnes pour 2 cellules : les rangées Stock et Statut se retrouvaient
  // à droite des tarifs au lieu de passer à la ligne.
  const visibleDays = daysOfMonth.slice(startIndex, startIndex + daysPerPage);

  const selectedCount = editType ? selectedDays[editType]?.length || 0 : 0;

  // 🆕 PÉRIODES — tarif visé par la sélection en cours, et son unité.
  // editRatePlan porte l'id du plan cliqué dans la grille.
  const selectedPlan = editType === "tarif" && editRatePlan
    ? ratePlans.find(p => p._id?.toString() === editRatePlan?.toString())
    : null;
  const selectedUnit = selectedPlan ? unitOf(selectedPlan.stayType) : UNIT.short_stay;
  const selectedIsPeriod = selectedPlan ? isPeriodPlan(selectedPlan.stayType) : false;

  return (
    <Box sx={{ p: 4, backgroundColor: "#FFFFFF", minHeight: "100vh" }}>

      {/* HEADER */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Calendrier</Typography>
          <Typography variant="body2" color="text.secondary">
            Gérez vos prix, stocks et disponibilités
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setBulkEditOpen(true)}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Modification groupée
          </Button>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={() => changeMonth(-1)} size="small">
              <ChevronLeft size={20} />
            </IconButton>
            <Typography fontWeight={600} sx={{ minWidth: 140, textAlign: "center", textTransform: "capitalize" }}>
              {monthLabel}
            </Typography>
            <IconButton onClick={() => changeMonth(1)} size="small">
              <ChevronRight size={20} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* PAGINATION JOURS */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Button
          size="small"
          disabled={startIndex === 0}
          onClick={() => setStartIndex(Math.max(0, startIndex - daysPerPage))}
          sx={{ textTransform: "none" }}
        >
          ← Jours précédents
        </Button>

        <Typography variant="body2" color="text.secondary">
          Jours {startIndex + 1} - {Math.min(startIndex + daysPerPage, daysOfMonth.length)} / {daysOfMonth.length}
        </Typography>

        <Button
          size="small"
          disabled={startIndex + daysPerPage >= daysOfMonth.length}
          onClick={() => setStartIndex(startIndex + daysPerPage)}
          sx={{ textTransform: "none" }}
        >
          Jours suivants →
        </Button>
      </Box>

      {/* HEADER JOURS */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: `200px repeat(${visibleDays.length}, minmax(64px, 1fr))`,
        mb: 1,
        position: "sticky",
        top: 0,
        backgroundColor: "white",
        zIndex: 10
      }}>
        <Box />
        {visibleDays.map((dateStr, i) => {
          const date = new Date(`${dateStr}T12:00:00`);
          const isToday = date.toDateString() === new Date().toDateString();
          const isWeekend = [0, 6].includes(date.getDay());

          return (
            <Box
              key={i}
              sx={{
                textAlign: "center",
                py: 1.25,
                backgroundColor: isToday ? "var(--k-green-soft)" : isWeekend ? "#F8FAFC" : "transparent",
                borderRadius: 1
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                {date.toLocaleDateString("fr-FR", { weekday: "short" })}
              </Typography>
              <Typography variant="body2" fontWeight={isToday ? 700 : 500}>
                {date.getDate()}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* GRIDS */}
      {loading ? (
        <Typography>Chargement...</Typography>
      ) : rooms.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography color="text.secondary">Aucune chambre configurée</Typography>
        </Box>
      ) : (
        rooms.map(room => (
          <InventoryGrid
            key={room.roomId}
            room={room}
            daysOfMonth={daysOfMonth}
            startIndex={startIndex}
            daysPerPage={daysPerPage}
            selectedDays={selectedDays}
            setSelectedDays={setSelectedDays}
            editType={editType}
            setEditType={setEditType}
            editModeRoom={editModeRoom}
            setEditModeRoom={setEditModeRoom}
            isSelecting={isSelecting}
            setIsSelecting={setIsSelecting}
            selectionStart={selectionStart}
            setSelectionStart={setSelectionStart}
            editRatePlan={editRatePlan}
            setEditRatePlan={setEditRatePlan}
            bulkDrawer={bulkDrawer}
            setBulkDrawer={setBulkDrawer}
            bulkValue={bulkValue}
            setBulkValue={setBulkValue}
            handleApplyBulk={handleApplyBulk}
            setActivePage={setActivePage}
            allRatePlans={ratePlans}
          />
        ))
      )}

      {/* FLOATING ACTION BAR */}
      {selectedCount > 0 && (
        <Box sx={{
          position: "fixed",
          bottom: 24,
          left: "50%",
          transform: "translateX(-50%)",
          backgroundColor: "white",
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          border: "1px solid #E2E8F0",
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          zIndex: 100
        }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="body2" fontWeight={600}>
              {selectedCount} jour(s) sélectionné(s)
            </Typography>
            {/* 🆕 PÉRIODES — rappel du tarif visé et de son unité */}
            {editType === "tarif" && selectedPlan && (
              <Typography variant="caption" sx={{ color: selectedIsPeriod ? "var(--k-green)" : "#94A3B8" }}>
                {selectedPlan.name} · prix / {selectedUnit.label}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <input
              type={editType === "status" ? "text" : "number"}
              placeholder={
                editType === "tarif"
                  // 🆕 PÉRIODES — le placeholder porte l'unité : c'est la
                  // dernière chance de voir qu'on fixe un mois, pas une nuit.
                  ? `Nouveau prix (€ / ${selectedUnit.label})`
                  : editType === "inventory"
                    ? "Nombre de chambres"
                    : "opened / closed"
              }
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid #E2E8F0",
                fontSize: 14,
                outline: "none",
                width: 200
              }}
            />

            {/* 🆕 DEVISES — équivalent KMF en direct */}
            {editType === "tarif" && newValue > 0 && (
              <Typography variant="caption" sx={{ color: "#64748B", pl: 0.5 }}>
                ≈ {formatKmf(eurToKmf(newValue))} KMF / {selectedUnit.label}
                {/* 🆕 PÉRIODES — équivalent par nuit, indicatif */}
                {selectedIsPeriod && ` · soit ≈ ${Math.round(Number(newValue) / selectedUnit.nights)} €/nuit`}
              </Typography>
            )}
          </Box>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!newValue}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Appliquer
          </Button>

          <Button
            variant="text"
            onClick={resetSelection}
            sx={{ textTransform: "none" }}
          >
            Annuler
          </Button>
        </Box>
      )}

      {/* BULK EDIT DRAWER */}
      <BulkEditDrawer
        open={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        rooms={rooms}
        onApply={handleBulkApply}
      />

    </Box>
  );
};

export default Calendar;